import { useMemo } from 'react';
import type { QrDoProduto } from '../../api/produtos.js';
import type { TamanhoDeEtiqueta } from './etiqueta.js';
import { caminhoDaMatriz, layoutDaEtiqueta, MARGEM_EM_MODULOS } from './etiqueta.js';
import './DesenhoDaEtiqueta.css';

type Props = {
  readonly tamanho: TamanhoDeEtiqueta;
  readonly qr: QrDoProduto;
  /**
   * Na folha de impressão o SVG tem largura e altura em milímetros — é isso
   * que faz o QR sair com 40 ou 50 mm de verdade. Na pré-visualização ele se
   * ajusta ao cartão.
   */
  readonly emMilimetros?: boolean;
};

/**
 * Uma etiqueta inteira, como um SVG em milímetros (viewBox = a etiqueta).
 *
 * A mesma peça desenha a pré-visualização e a folha impressa: o que a pessoa
 * confere na tela é o que sai na impressora. O `codPro` é o texto que o
 * middleware devolveu na matriz, e não o digitado — os dois são iguais por
 * contrato (`obterQrDoProduto` recusa o contrário).
 */
export function DesenhoDaEtiqueta({ tamanho, qr, emMilimetros = false }: Props) {
  const layout = useMemo(() => layoutDaEtiqueta(tamanho), [tamanho]);
  const caminho = useMemo(() => caminhoDaMatriz(qr.modulos, qr.n), [qr.modulos, qr.n]);

  // Os módulos começam depois da margem branca, que fica livre de texto e borda.
  const origemX = layout.simbolo.xMm + MARGEM_EM_MODULOS * layout.moduloMm;
  const origemY = layout.simbolo.yMm + MARGEM_EM_MODULOS * layout.moduloMm;

  return (
    <svg
      className={emMilimetros ? 'etiqueta' : 'etiqueta etiqueta--previa'}
      role="img"
      aria-label={`Etiqueta do produto ${qr.codPro}`}
      viewBox={`0 0 ${String(layout.larguraMm)} ${String(layout.alturaMm)}`}
      {...(emMilimetros
        ? { width: `${String(layout.larguraMm)}mm`, height: `${String(layout.alturaMm)}mm` }
        : {})}
      shapeRendering="crispEdges"
    >
      <rect className="etiqueta__papel" width={layout.larguraMm} height={layout.alturaMm} />
      <path
        className="etiqueta__tinta"
        d={caminho}
        transform={`translate(${String(origemX)} ${String(origemY)}) scale(${String(layout.moduloMm)})`}
      />
      <text
        className="etiqueta__codigo"
        x={layout.texto.centroMm}
        y={layout.texto.linhaDeBaseMm}
        fontSize={layout.texto.fonteMm}
        textAnchor="middle"
      >
        {qr.codPro}
      </text>
    </svg>
  );
}
